// Extraído de PeticionDetail.jsx (refactor mecánico, sin cambios funcionales)
import React, { useState } from "react";
import api, { ORIGEN_LABELS, ORIGENES_ACTIVOS } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Wrench, Image as ImageIcon, ChevronRight, Copy } from "lucide-react";

// ============ INLINE "Elementos a montar" para líneas SOLO MONTAJE (Iter 60) ============
// Permite añadir/eliminar elementos directamente desde la tarjeta de la línea, sin abrir el
// modal pesado de componentes. Solo guarda los datos mínimos relevantes para montaje:
//   - Nombre, Cantidad, Tamaño (texto), Origen del material, Imagen de referencia, Notas
// Internamente crea registros en la colección Componente (mismo endpoint POST /componentes)
// con coste/precio = 0 y tipo_plantilla = "libre".
export function ElementosMontajeInline({ linea, elementos, onChanged }) {
  const [expanded, setExpanded] = useState(elementos.length === 0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    nombre: "", cantidad: 1, tamano: "",
    origen: "proveedor_a_nave", notas: "", imagen_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Iter 60.1 — Autocomplete elementos previos
  const [sugerencias, setSugerencias] = useState([]);
  const [top, setTop] = useState([]);
  const [showSug, setShowSug] = useState(false);

  React.useEffect(() => {
    let alive = true;
    if (!expanded) return;
    api.get(`/lineas/${linea.id}/sugerencias-elementos`)
      .then(({ data }) => {
        if (!alive) return;
        setSugerencias(data.sugerencias || []);
        setTop(data.top || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [linea.id, expanded]);

  // Filtrar sugerencias por lo que el usuario está escribiendo
  const filteredSug = React.useMemo(() => {
    const q = form.nombre.trim().toLowerCase();
    if (!q) return [];
    return sugerencias.filter((s) => s.nombre.toLowerCase().includes(q) && s.nombre.toLowerCase() !== q).slice(0, 6);
  }, [form.nombre, sugerencias]);

  const aplicarSugerencia = (s) => {
    setForm((f) => ({
      ...f,
      nombre: s.nombre,
      tamano: s.tamano || f.tamano,
      origen: s.origen || f.origen,
      imagen_url: s.imagen_url || f.imagen_url,
    }));
    setShowSug(false);
  };

  const clonarTop = (s) => {
    if (!adding) setAdding(true);
    setForm({
      nombre: s.nombre,
      cantidad: 1,
      tamano: s.tamano || "",
      origen: s.origen || "proveedor_a_nave",
      imagen_url: s.imagen_url || "",
      notas: "",
    });
  };

  const reset = () => setForm({
    nombre: "", cantidad: 1, tamano: "",
    origen: "proveedor_a_nave", notas: "", imagen_url: "",
  });

  const handleImg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10_000_000) { toast.error("Imagen demasiado grande (máx 10 MB)."); return; }
    setUploading(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/cloudinary");
      const { secure_url } = await uploadToCloudinary(file, "dnf/componentes");
      setForm((f) => ({ ...f, imagen_url: secure_url }));
    } catch (err) {
      toast.error("Error al subir: " + (err.message || err));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return;
    setSaving(true);
    try {
      await api.post("/componentes", {
        linea_id: linea.id,
        nombre: form.nombre.trim(),
        cantidad: parseFloat(form.cantidad) || 1,
        tamano: form.tamano.trim() || null,
        origen: form.origen,
        notas: form.notas.trim() || null,
        imagen_url: form.imagen_url || null,
        tipo_plantilla: "libre",
        coste_unitario: 0,
        precio_unitario: 0,
        estado: "pendiente",
      });
      toast.success("Elemento añadido");
      reset();
      setAdding(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al añadir elemento");
    } finally { setSaving(false); }
  };

  const remove = async (cid) => {
    if (!confirm("¿Eliminar este elemento?")) return;
    try {
      await api.delete(`/componentes/${cid}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="mt-3 bg-amber-500/[0.03] border border-amber-500/20 rounded-lg p-3"
      data-testid={`elementos-montaje-${linea.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <button type="button" onClick={() => setExpanded((v) => !v)}
          data-testid={`elementos-toggle-${linea.id}`}
          className="text-[11px] uppercase tracking-wider text-amber-300 hover:text-amber-200 inline-flex items-center gap-1.5 font-semibold">
          <Wrench className="w-3 h-3" />
          {elementos.length === 0 ? "Elementos a montar" : `Elementos a montar (${elementos.length})`}
          <ChevronRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
        </button>
        {expanded && !adding && (
          <button type="button" onClick={() => setAdding(true)}
            data-testid={`elemento-add-btn-${linea.id}`}
            className="text-[11px] text-amber-300 hover:text-amber-200 inline-flex items-center gap-1">
            <Plus className="w-3 h-3" /> Añadir elemento
          </button>
        )}
      </div>
      {expanded && (
        <>
          {expanded && top.length > 0 && !adding && (
            <div className="mb-2 flex flex-wrap gap-1.5" data-testid={`elementos-top-${linea.id}`}>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 self-center">Más usados:</span>
              {top.map((s) => (
                <button key={s.nombre} type="button" onClick={() => clonarTop(s)}
                  data-testid={`elemento-quickclone-${linea.id}`}
                  className="text-[11px] bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 text-violet-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                  <Copy className="w-3 h-3" />
                  {s.nombre.length > 28 ? s.nombre.slice(0, 26) + "…" : s.nombre}
                  {s.count > 1 && <span className="text-violet-400/70">×{s.count}</span>}
                </button>
              ))}
            </div>
          )}
          {elementos.length === 0 && !adding && (
            <div className="text-[11px] text-zinc-500 italic">
              Sin elementos. Pulsa <span className="text-amber-300">+ Añadir elemento</span> para registrar qué hay que montar.
            </div>
          )}
          {elementos.length > 0 && (
            <div className="space-y-1.5 mb-2">
              {elementos.map((el) => (
                <div key={el.id} data-testid={`elemento-row-${el.id}`}
                  className="flex items-center gap-2 bg-zinc-900/40 rounded px-2 py-1.5 text-xs">
                  {el.imagen_url ? (
                    <img src={el.imagen_url} alt="" className="w-9 h-9 rounded object-cover border border-zinc-700 shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-700 shrink-0">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 truncate font-medium">{el.nombre}</div>
                    <div className="text-[10px] text-zinc-500 flex flex-wrap gap-x-2">
                      <span>×{el.cantidad}</span>
                      {el.tamano && <span>{el.tamano}</span>}
                      <span className={`px-1.5 rounded ${(ORIGEN_LABELS[el.origen] || ORIGEN_LABELS.proveedor_a_nave).color}`}>
                        {(ORIGEN_LABELS[el.origen] || ORIGEN_LABELS.proveedor_a_nave).label}
                      </span>
                      {el.notas && <span className="italic text-zinc-600 truncate">📝 {el.notas}</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => remove(el.id)}
                    data-testid={`elemento-delete-${el.id}`}
                    className="text-zinc-500 hover:text-red-400 shrink-0 p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {adding && (
            <form onSubmit={submit} className="bg-zinc-900/60 border border-zinc-800 rounded p-2 space-y-2">
              <div className="grid grid-cols-12 gap-2">
                <div className="col-span-12 sm:col-span-5 relative">
                  <Input value={form.nombre} required autoFocus
                    data-testid={`elemento-form-nombre-${linea.id}`}
                    onChange={(e) => { setForm({ ...form, nombre: e.target.value }); setShowSug(true); }}
                    onFocus={() => setShowSug(true)}
                    onBlur={() => setTimeout(() => setShowSug(false), 200)}
                    placeholder="Nombre / qué hay que montar"
                    className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                  {showSug && filteredSug.length > 0 && (
                    <div data-testid={`elemento-sugerencias-${linea.id}`}
                      className="absolute z-30 top-full left-0 right-0 mt-1 bg-zinc-950 border border-violet-500/40 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredSug.map((s) => (
                        <button key={s.nombre} type="button"
                          onMouseDown={(e) => { e.preventDefault(); aplicarSugerencia(s); }}
                          className="w-full text-left px-2 py-1.5 hover:bg-violet-500/10 text-xs text-zinc-200 border-b border-zinc-900 last:border-b-0 flex items-center justify-between gap-2">
                          <span className="truncate flex items-center gap-1.5">
                            {s.imagen_url && <img src={s.imagen_url} alt="" className="w-5 h-5 rounded object-cover" />}
                            {s.nombre}
                          </span>
                          <span className="text-[10px] text-zinc-500 shrink-0">
                            {s.tamano && <span className="mr-2">{s.tamano}</span>}
                            usado ×{s.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="col-span-4 sm:col-span-2">
                  <Input type="number" step="0.01" value={form.cantidad}
                    data-testid={`elemento-form-cantidad-${linea.id}`}
                    onChange={(e) => setForm({ ...form, cantidad: e.target.value })}
                    placeholder="Cant."
                    className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                </div>
                <div className="col-span-8 sm:col-span-5">
                  <Input value={form.tamano}
                    data-testid={`elemento-form-tamano-${linea.id}`}
                    onChange={(e) => setForm({ ...form, tamano: e.target.value })}
                    placeholder='Tamaño · ej. "120cm" "A1"'
                    className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Select value={form.origen} onValueChange={(v) => setForm({ ...form, origen: v })}>
                    <SelectTrigger data-testid={`elemento-form-origen-${linea.id}`}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                      {ORIGENES_ACTIVOS.filter((o) => o !== "produccion_propia").map((k) => (
                        <SelectItem key={k} value={k}>
                          {ORIGEN_LABELS[k]?.label || k}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-12 sm:col-span-6 flex items-center gap-2">
                  <input type="file" accept="image/*" onChange={handleImg} disabled={uploading}
                    data-testid={`elemento-form-img-${linea.id}`}
                    className="text-[11px] text-zinc-300 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-violet-500/20 file:text-violet-200 hover:file:bg-violet-500/30" />
                  {uploading && <span className="text-[10px] text-violet-300">Subiendo…</span>}
                  {form.imagen_url && (
                    <div className="relative">
                      <img src={form.imagen_url} alt="" className="w-8 h-8 object-cover rounded border border-violet-500/50" />
                      <button type="button" onClick={() => setForm({ ...form, imagen_url: "" })}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] leading-none">×</button>
                    </div>
                  )}
                </div>
                <div className="col-span-12">
                  <Input value={form.notas}
                    data-testid={`elemento-form-notas-${linea.id}`}
                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    placeholder="Notas (opcional)"
                    className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" size="sm" variant="outline"
                  onClick={() => { setAdding(false); reset(); }}
                  className="h-7 text-xs bg-transparent border-zinc-700">
                  Cancelar
                </Button>
                <Button type="submit" size="sm" disabled={saving || uploading || !form.nombre.trim()}
                  data-testid={`elemento-form-submit-${linea.id}`}
                  className="h-7 text-xs bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
                  {saving ? "Guardando…" : "Añadir"}
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  );
}
